'use client'

/**
 * Internal dev tool: inject fake free bets / boosters into the live inventory so
 * the real UI (grant banners, sidebar, bet panels) can be exercised without a
 * backend grant. Items are minted as DTOs and run through the real mapper, so
 * what you see matches a genuine `inventory-item:added` event.
 *
 * Visibility (never renders in production):
 *   - development:        always on (collapsed pill, bottom-left)
 *   - staging / preview:  add `?fbdebug=1` to the URL (per-user opt-in). NOT enabled by a
 *                         shared env var — that would show it to clients testing on staging.
 *
 * Injected items are tagged `debug`; they are for visual testing only and will
 * be rejected by the backend if actually used to place a bet.
 */

import { useEffect, useState } from 'react'
import { fromInventoryItemsDTO } from 'src/features/inventory/mappers/inventory.mapper'
import { useInventoryActions, useInventoryItemCount } from 'src/features/inventory/store'
import { useGetCurrentGameId } from 'src/store/game'
import { useGetCurrentPlayer } from 'src/store/player'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import {
  buildDebugInventoryDtos,
  type BuildDebugInventoryParams,
  type DebugItemType
} from './buildDebugInventory'
import { isInventoryDebugEnabled } from './isInventoryDebugEnabled'

function useInventoryDebugEnabled(): boolean {
  const [enabled, setEnabled] = useState(false)
  useEffect(() => {
    // Runs client-side only, so window.location is available. Local dev is
    // detected via NODE_ENV (development) rather than NEXT_PUBLIC_ENV, which is
    // set to `staging` in .env.local — see isInventoryDebugEnabled.
    setEnabled(
      isInventoryDebugEnabled({
        env: process.env.NEXT_PUBLIC_ENV,
        nodeEnv: process.env.NODE_ENV,
        fbdebugParam: new URLSearchParams(window.location.search).get('fbdebug')
      })
    )
  }, [])
  return enabled
}

type GameScope = 'current' | 'all' | 'custom'

const s = {
  wrap: {
    position: 'fixed',
    left: 12,
    bottom: 12,
    zIndex: 2147483646,
    fontFamily: 'monospace',
    fontSize: 12,
    color: '#e6e6e6'
  } as const,
  pill: {
    background: '#10141c',
    border: '1px solid #2bd17e',
    color: '#2bd17e',
    borderRadius: 999,
    padding: '6px 12px',
    cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(0,0,0,0.4)'
  } as const,
  panel: {
    width: 260,
    background: '#10141c',
    border: '1px solid #2a3344',
    borderRadius: 10,
    padding: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  } as const,
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 } as const,
  input: {
    width: 110,
    background: '#0a0d13',
    border: '1px solid #2a3344',
    color: '#e6e6e6',
    borderRadius: 6,
    padding: '4px 6px'
  } as const,
  btn: {
    flex: 1,
    background: '#2bd17e',
    border: 'none',
    color: '#06281a',
    fontWeight: 700,
    borderRadius: 6,
    padding: '7px 8px',
    cursor: 'pointer'
  } as const,
  btnGhost: {
    flex: 1,
    background: 'transparent',
    border: '1px solid #46506a',
    color: '#cdd5e4',
    borderRadius: 6,
    padding: '7px 8px',
    cursor: 'pointer'
  } as const,
  label: { color: '#8b97ad' } as const,
  title: { color: '#2bd17e', fontWeight: 700 } as const,
  note: { color: '#8b97ad', lineHeight: 1.35 } as const
}

export function InventoryDebugPanel() {
  const enabled = useInventoryDebugEnabled()
  const [open, setOpen] = useState(false)
  const { addInventoryItem, reset } = useInventoryActions()
  const itemCount = useInventoryItemCount()
  const currentGameId = useGetCurrentGameId()
  const player = useGetCurrentPlayer()
  const playerCurrency = isAuthenticatedPlayer(player) ? player.balance.currency : 'USD'
  const playerId = isAuthenticatedPlayer(player) ? player.id : 'debug-player'

  const [type, setType] = useState<DebugItemType>('free_bet')
  const [count, setCount] = useState(10)
  const [status, setStatus] = useState<'claimable' | 'claimed'>('claimable')
  const [scope, setScope] = useState<GameScope>('current')
  const [customGameId, setCustomGameId] = useState('')
  const [amount, setAmount] = useState(5)
  const [currency, setCurrency] = useState(playerCurrency)
  const [expiresInHours, setExpiresInHours] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  // Keep the currency default in step with the player once it loads.
  useEffect(() => {
    setCurrency(playerCurrency)
  }, [playerCurrency])

  if (!enabled) return null

  const resolveApplicableGames = (): string[] => {
    if (scope === 'all') return []
    if (scope === 'custom') return customGameId.trim() ? [customGameId.trim()] : []
    return currentGameId ? [currentGameId] : []
  }

  const grant = (override?: Partial<BuildDebugInventoryParams>) => {
    try {
      const dtos = buildDebugInventoryDtos({
        type,
        count,
        status,
        applicableGames: resolveApplicableGames(),
        seed: Date.now().toString(36),
        nowIso: new Date().toISOString(),
        playerId,
        amount,
        currency,
        expiresInHours: expiresInHours.trim() === '' ? null : Number(expiresInHours),
        ...override
      })
      // Inject one at a time, mirroring how real grants arrive (one
      // `inventory-item:added` event per item) so the UI reacts identically.
      fromInventoryItemsDTO(dtos).forEach((item) => {
        addInventoryItem(item)
      })
      setMessage(`Granted ${dtos.length} × ${override?.type ?? type}`)
    } catch (err) {
      setMessage(`Error: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  if (!open) {
    return (
      <div style={s.wrap}>
        <button
          onClick={() => {
            setOpen(true)
          }}
          style={s.pill}
          type="button"
        >
          🎟 FB debug{itemCount > 0 ? ` · ${itemCount}` : ''}
        </button>
      </div>
    )
  }

  return (
    <div style={s.wrap}>
      <div style={s.panel}>
        <div style={s.row}>
          <span style={s.title}>Free-bet debug</span>
          <button
            onClick={() => {
              setOpen(false)
            }}
            style={{ ...s.btnGhost, flex: 'none', padding: '2px 8px' }}
            type="button"
          >
            ✕
          </button>
        </div>

        <div style={s.row}>
          <span style={s.label}>Type</span>
          <select
            onChange={(e) => {
              setType(e.target.value as DebugItemType)
            }}
            style={s.input}
            value={type}
          >
            <option value="free_bet">Free bet</option>
            <option value="booster">Booster</option>
          </select>
        </div>

        <div style={s.row}>
          <span style={s.label}>Count</span>
          <input
            min={1}
            onChange={(e) => {
              setCount(Number(e.target.value))
            }}
            style={s.input}
            type="number"
            value={count}
          />
        </div>

        <div style={s.row}>
          <span style={s.label}>Status</span>
          <select
            onChange={(e) => {
              setStatus(e.target.value as 'claimable' | 'claimed')
            }}
            style={s.input}
            value={status}
          >
            <option value="claimable">claimable (grant flow)</option>
            <option value="claimed">claimed (ready)</option>
          </select>
        </div>

        {type === 'free_bet' && (
          <>
            <div style={s.row}>
              <span style={s.label}>Amount</span>
              <input
                min={0}
                onChange={(e) => {
                  setAmount(Number(e.target.value))
                }}
                style={s.input}
                type="number"
                value={amount}
              />
            </div>
            <div style={s.row}>
              <span style={s.label}>Currency</span>
              <input
                onChange={(e) => {
                  setCurrency(e.target.value.toUpperCase())
                }}
                style={s.input}
                value={currency}
              />
            </div>
          </>
        )}

        <div style={s.row}>
          <span style={s.label}>Games</span>
          <select
            onChange={(e) => {
              setScope(e.target.value as GameScope)
            }}
            style={s.input}
            value={scope}
          >
            <option value="current">this game</option>
            <option value="all">all games</option>
            <option value="custom">custom id…</option>
          </select>
        </div>
        {scope === 'custom' && (
          <div style={s.row}>
            <span style={s.label}>Game id</span>
            <input
              onChange={(e) => {
                setCustomGameId(e.target.value)
              }}
              placeholder="gameId"
              style={s.input}
              value={customGameId}
            />
          </div>
        )}

        <div style={s.row}>
          <span style={s.label}>Expiry (h)</span>
          <input
            onChange={(e) => {
              setExpiresInHours(e.target.value)
            }}
            placeholder="none"
            style={s.input}
            value={expiresInHours}
          />
        </div>

        <div style={s.row}>
          <button
            onClick={() => {
              grant()
            }}
            style={s.btn}
            type="button"
          >
            Grant
          </button>
          <button
            onClick={() => {
              reset()
            }}
            style={s.btnGhost}
            type="button"
          >
            Clear all
          </button>
        </div>

        <div style={s.row}>
          <button
            onClick={() => {
              grant({ type: 'free_bet', count: 10, status: 'claimable', amount: 5 })
            }}
            style={s.btnGhost}
            type="button"
          >
            +10 × {currency} 5
          </button>
          <button
            onClick={() => {
              grant({ type: 'free_bet', count: 1, status: 'claimable', amount: 10 })
            }}
            style={s.btnGhost}
            type="button"
          >
            +1 × {currency} 10
          </button>
        </div>

        <div style={s.note}>
          In inventory now: <b>{itemCount}</b> claimed.
          {message ? (
            <>
              <br />
              {message}
            </>
          ) : null}
        </div>
      </div>
    </div>
  )
}
